/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { submitApplication } from '../../../lib/api/submitApplication';
import logo from '../../assets/logo.svg';
import type { ApplicationSubmission } from '../../../types/application';
import * as Sentry from '@sentry/react';
import { usePlausible } from '../../../hooks/usePlausible';
import { keyframes } from '@emotion/react';

// Define the animation
const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

// Add validation types
type PhoneNumber = string & { __brand: 'PhoneNumber' };

// Define languages as a constant at the top level
const SUPPORTED_LANGUAGES = ['English', 'Spanish', 'Both', 'Other'] as const;
type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

interface ApplicationFormData {
  // Required Personal Info
  firstName: string;
  lastName: string;
  email: string;
  location: 'US' | 'non-US';
  
  // Required Contact
  contact: {
    phoneNumber: string;
    discord?: string;  // Made optional
  };
  
  // Required Professional Info
  languagesSpoken: SupportedLanguage[];
  showsFace: boolean;
  portfolio: string[];  // Just one required link
  
  // Optional fields - all made optional
  bestContent?: string[];
  businessCount?: string;
  lifetimeViews?: string;
  lifetimeGmv?: string;
  referralSource?: string;
}

// Validation functions
const validatePhoneNumber = (phone: string): boolean => {
  const cleanNumber = phone.replace(/[\s()-]/g, '');
  return /^\+?[1-9]\d{1,14}$/.test(cleanNumber);
};

const validateURL = (url: string): boolean => {
  try {
    // Check if string is empty or just whitespace
    if (!url.trim()) return false;
    
    // Try to construct URL - this will throw if invalid
    new URL(url);
    
    // Additional check to ensure it starts with http:// or https://
    return /^https?:\/\//i.test(url);
  } catch {
    return false;
  }
};

// Common class for all text inputs and selects
const inputClass = "mt-1 block w-full rounded-md border-0 bg-[#5E150F] px-3 py-2 min-h-[48px] text-[#F7E8CC] placeholder:text-[#F7E8CC]/50 focus:ring-2 focus:ring-[#F7E8CC]/10";

// Update the inputClass for selects to include more padding on the right for the arrow
const selectClass = "mt-1 block w-full rounded-md border-0 bg-[#5E150F] px-3 pr-8 py-2 min-h-[48px] text-[#F7E8CC] placeholder:text-[#F7E8CC]/50 focus:ring-2 focus:ring-[#F7E8CC]/10 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20stroke%3D%22%23F7E8CC%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%20d%3D%22m6%208%204%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1.5em_1.5em] bg-[right_0.75rem_center] bg-no-repeat";

// Common class for radio buttons
const radioClass = "appearance-none h-4 w-4 rounded-full border-2 border-[#F7E8CC]/40 bg-[#5E150F] checked:bg-[#F7E8CC] checked:ring-[#6E1912] checked:ring-2 checked:ring-inset hover:bg-[#F7E8CC] focus:ring-[#6E1912] focus:ring-offset-[#7B1C14] focus:ring-2 focus:outline-none cursor-pointer";

// Common class for labels
const labelClass = "block text-base font-medium text-[#F7E8CC] mb-1";

// Common class for error messages
const errorClass = "mt-1 text-sm text-red-300";

// Common class for section containers
const sectionClass = "bg-[#6E1912] rounded-lg shadow-lg p-8";

// Common class for section headers
const sectionHeaderClass = "text-xl font-semibold text-[#F7E8CC] mb-6";

// Common class for helper text
const helperTextClass = "mt-1 text-sm text-[#F7E8CC]/50";

// Common class for buttons
const buttonClass = "mt-2 inline-flex items-center px-3 py-2 border border-[#F7E8CC]/10 rounded-md text-[#F7E8CC] bg-[#5E150F] hover:bg-[#4A110D] focus:outline-none focus:ring-2 focus:ring-[#F7E8CC]/10";

// Add this helper function before the component
const categorizeLinksByType = (links: string[]) => {
  const categorized = {
    portfolio: [] as string[],
    bestContent: [] as string[]
  };

  links.forEach(link => {
    // Content-specific URLs typically contain these patterns
    const isContentUrl = /\/(video|post|reel|watch|shorts)\//.test(link) || 
                        /\/p\//.test(link);  // Instagram post pattern
    
    if (isContentUrl) {
      categorized.bestContent.push(link);
    } else {
      // Profile/portfolio links are typically simpler URLs
      categorized.portfolio.push(link);
    }
  });

  return categorized;
};


interface PlausibleEvents {
  'Form View': undefined;
  'Form Abandoned': {
    lastSection: string;
    timeSpent: number;
    completedFields: string[];
  };
  'Basic Info Completed': undefined;
  'Brand Info Completed': undefined;
  'Content Creation Completed': undefined;
  'Contact Info Completed': undefined;
  'Form Field Interaction': {
    field: string;
    section: string;
    isComplete: boolean;
  };
  [key: string]: any;  // Add index signature for other events
}

interface FormErrors extends Partial<Record<keyof ApplicationFormData, string>> {
  'contact.phone'?: string;
}

function ApplicationFormComponent() {
  const { trackEvent } = usePlausible<PlausibleEvents>();
  const startTime = React.useRef(Date.now());
  const lastInteraction = React.useRef(Date.now());
  const visitedSections = React.useRef(new Set<string>());
  const completedFields = React.useRef(new Set<string>());
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<ApplicationFormData>({
    firstName: '',
    lastName: '',
    email: '',
    location: 'US',
    languagesSpoken: ['English'],
    showsFace: true,
    contact: {
      phoneNumber: '',
    },
    portfolio: [''],  // Start with just one field
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const subdomain = 'spicycubes'; // Default brand

  // Track initial form view
  React.useEffect(() => {
    trackEvent('Form View', undefined, 0);

    const handleBeforeUnload = () => {
      const timeSpent = Date.now() - startTime.current;
      if (!isSubmitting && completedFields.current.size > 0) {
        trackEvent('Form Abandoned', {
          lastSection: Array.from(visitedSections.current).pop() || 'unknown',
          timeSpent,
          completedFields: Array.from(completedFields.current)
        }, 0); // No debounce for form abandonment
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Track section completion with separate events
  const trackSectionCompletion = (section: string) => {
    if (!visitedSections.current.has(section)) {
      visitedSections.current.add(section);
      
      switch(section) {
        case 'basic-info':
          trackEvent('Basic Info Completed', undefined, 500); // 0.5s debounce
          break;
        case 'brand-info':
          trackEvent('Brand Info Completed', undefined, 500);
          break;
        case 'content-creation':
          trackEvent('Content Creation Completed', undefined, 500);
          break;
        case 'contact':
          trackEvent('Contact Info Completed', undefined, 500);
          break;
      }
    }
  };

  // Enhanced field change tracking
  const handleFieldChange = (field: keyof ApplicationFormData, value: any) => {
    try {
      // Special handling for languagesSpoken to ensure it's always an array
      if (field === 'languagesSpoken') {
        setFormData(prev => ({
          ...prev,
          languagesSpoken: [value] // Wrap the single selection in an array
        }));
      } else {
        setFormData(prev => ({ ...prev, [field]: value }));
      }
      
      const section = getSectionForField(field);
      const isComplete = validateField(field, value);
      
      if (isComplete) {
        completedFields.current.add(field);
      }

      // Longer debounce for field interactions (2 seconds)
      trackEvent('Form Field Interaction', {
        field,
        section,
        isComplete
      }, 2000);

      // Check section completion
      trackSectionCompletion(section);

    } catch (error) {
      Sentry.captureException(error);
    }
  };

  // Simplified validation
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    
    // Only validate required fields
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.location) newErrors.location = 'Location is required';
    
    if (!formData.contact.phoneNumber) {
      newErrors['contact.phone'] = 'Phone number is required';
    } else if (!validatePhoneNumber(formData.contact.phoneNumber)) {
      newErrors['contact.phone'] = 'Please enter a valid phone number';
    }

    if (!formData.languagesSpoken.length) {
      newErrors.languagesSpoken = 'Please select at least one language';
    }

    // Validate all provided URLs
    const validUrls = formData.portfolio.filter(url => url.trim() && validateURL(url));
    if (validUrls.length === 0) {
      newErrors.portfolio = 'At least one valid link is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Update form field handlers to use the new error tracking
  const updateFormField = (field: keyof ApplicationFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    handleFieldChange(field, e.target.value);
  };

  // Simplify handleSubmit to only keep error tracking
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Filter out empty links and categorize them
      const validLinks = formData.portfolio.filter(link => link.trim());
      const { portfolio, bestContent } = categorizeLinksByType(validLinks);

      // Ensure we maintain the API contract
      const submissionData: ApplicationSubmission = {
        subdomain: subdomain!,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        contact: {
          phone: formData.contact.phoneNumber,
          discord: formData.contact.discord,
          location: formData.location
        },
        application: {
          languagesSpoken: formData.languagesSpoken,
          showsFace: formData.showsFace,
          portfolio: portfolio,
          bestContent: bestContent,
          businessCount: formData.businessCount,
          lifetimeViews: formData.lifetimeViews,
          lifetimeGmv: formData.lifetimeGmv,
          referralSource: formData.referralSource
        }
      };

      await submitApplication(submissionData);
      
      // No debounce for form submission
      trackEvent('Form Submitted', undefined, 0);

      navigate('/apply/thank-you');
    } catch (error) {
      Sentry.captureException(error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper functions
  const getSectionForField = (field: keyof ApplicationFormData): string => {
    const sectionMap: Record<string, string> = {
      firstName: 'basic-info',
      lastName: 'basic-info',
      email: 'basic-info',
      location: 'basic-info',
      businessCount: 'brand-info',
      lifetimeViews: 'brand-info',
      lifetimeGmv: 'brand-info',
      portfolio: 'content-creation',
      bestContent: 'content-creation',
      showsFace: 'content-creation',
      'contact.phoneNumber': 'contact',
      'contact.discord': 'contact'
    };
    return sectionMap[field] || 'unknown';
  };

  const getSectionFields = (section: string): string[] => {
    const fieldMap: Record<string, string[]> = {
      'basic-info': ['firstName', 'lastName', 'email', 'location'],
      'brand-info': ['businessCount', 'lifetimeViews', 'lifetimeGmv'],
      'content-creation': ['portfolio', 'bestContent', 'showsFace'],
      'contact': ['contact.phoneNumber', 'contact.discord']
    };
    return fieldMap[section] || [];
  };

  const validateField = (field: string, value: any): boolean => {
    // Add field-specific validation logic
    switch (field) {
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      case 'contact.phoneNumber':
        return validatePhoneNumber(value);
      case 'portfolio':
        return Array.isArray(value) && value.some(url => validateURL(url));
      default:
        return Boolean(value);
    }
  };

  // Add function to handle multiple links
  const handleAddLink = () => {
    setFormData(prev => ({
      ...prev,
      portfolio: [...prev.portfolio, '']
    }));
  };

  const handleRemoveLink = (index: number) => {
    setFormData(prev => ({
      ...prev,
      portfolio: prev.portfolio.filter((_, i) => i !== index)
    }));
  };

  const handleLinkChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      portfolio: prev.portfolio.map((link, i) => i === index ? value : link)
    }));
  };

  return (
    <div className="min-h-screen bg-[#7B1C14] py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="text-center mb-8">
          <img 
            src={logo} 
            alt="Spicy Cubes" 
            className="h-16 sm:h-20 md:h-24 mx-auto"
          />
        </div>
        
        <div className="bg-[#6E1912] rounded-lg shadow-lg p-8">
          <div className="relative pb-6 mb-6 border-b border-[#F7E8CC]/10">
            <h1 className="text-3xl font-bold text-[#F7E8CC] text-center">Creator Application</h1>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-48 h-[2px] bg-gradient-to-r from-transparent via-[#F7E8CC]/10 to-transparent" />
          </div>

          <p className="mt-4 text-[#F7E8CC]/80">
            Dear Friend,
          </p>
          <p className="mt-4 text-[#F7E8CC]/80">
            I've got something here at Spicy Cubes that's so hot, so spicy, 
            it'll make your heart race faster than a letter from the IRS. 
            We're on the hunt for creators who can think beyond the ordinary. Our top earners are raking in thousands of dollars monthly, starting from scratch—no followers, just raw talent. 
            Here's proof it works:
            <br />
            <br />
            <a href="https://www.tiktok.com/@brittanylove77" className="font-bold text-[#FF4136] hover:text-[#FF2718] transition-colors">@spicy_tay </a>
            went viral on her 8th video achieving <span className="font-bold text-[#FF4136]">1.4M views</span> and earning <span className="font-bold text-[#FF4136]">$6.3k</span> comissions in her first month!
            <br />
            <br />
            <a href="https://www.tiktok.com/@wantspicycubes" className="font-bold text-[#FF851B] hover:text-[#FF6700] transition-colors">@wantspicycubes </a>
            has done <span className="font-bold text-[#FF851B]">8.9M views</span> with multiple <span className="font-bold text-[#FF851B]">$12k+ months</span>.
            <br />
            <br />
            <a href="https://www.tiktok.com/@insanespicycubes" className="font-bold text-[#FFB700] hover:text-[#FFA200] transition-colors">@insanespicycubes </a>
            did <span className="font-bold text-[#FFB700]">2M views</span> on one video, hitting <span className="font-bold text-[#FFB700]">$80k GMV</span> in just 2 days!
            <br />
            <br />
            Act fast! <span className="text-[#FFD700] font-bold" >Limited spots available</span>. 
            We provide 1:1 training, equipping you with our battle-tested strategies to go viral. 
            If you're the one for the job, <span className="text-[#FFD700] font-bold">fill out the form and we will get back to you within 24hrs!</span>
          </p>
          <br />
          <p className="mt-2 text-[#FFD700] font-bold text-center">TLDR;</p>
          <p className="mt-2 text-[#F7E8CC]/80 font-medium text-center">👇Apply now to earn👇</p>
          <p
            css={{
              backgroundImage: 'linear-gradient(90deg, #FF5B45, #FFD700, #FF5B45)',
              backgroundSize: '200% auto',
              color: 'transparent',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              animation: `${gradientMove} 3s linear infinite`,
            }}
            className="mt-2 font-bold text-center"
          >
            $500 - $1k/mo + 25% of sales<br />
            + other bonuses ;)
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info Section */}
          <div className={sectionClass}>
            <div className="space-y-6">
              <div>
                <label className={labelClass}>
                  Email <span className="text-red-300">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={updateFormField('email')}
                  placeholder="your@email.com"
                  className={inputClass}
                />
                {errors.email && <p className={errorClass}>{errors.email}</p>}
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>
                    First Name <span className="text-red-300">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={updateFormField('firstName')}
                    placeholder="First Name"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Last Name <span className="text-red-300">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={updateFormField('lastName')}
                    placeholder="Last Name"
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>
                  Are you based in the US? <span className="text-red-300">*</span>
                </label>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      name="location"
                      value="US"
                      checked={formData.location === 'US'}
                      onChange={updateFormField('location')}
                      className={radioClass}
                    />
                    <label className="ml-2 text-sm text-[#F7E8CC]">Yes</label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      name="location"
                      value="non-US"
                      checked={formData.location === 'non-US'}
                      onChange={updateFormField('location')}
                      className={radioClass}
                    />
                    <label className="ml-2 text-sm text-[#F7E8CC]">No</label>
                  </div>
                </div>
              </div>

              <div>
                <label className={labelClass}>
                  What language(s) do you speak? <span className="text-red-300">*</span>
                </label>
                <div className="mt-2 space-y-2">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <div key={lang} className="flex items-center">
                      <input
                        type="radio"
                        name="language"
                        value={lang}
                        checked={formData.languagesSpoken[0] === lang}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleFieldChange('languagesSpoken', lang);
                          }
                        }}
                        className={radioClass}
                      />
                      <label className="ml-2 text-sm text-[#F7E8CC]">{lang}</label>
                    </div>
                  ))}
                </div>
                {errors.languagesSpoken && (
                  <p className={errorClass}>{errors.languagesSpoken}</p>
                )}
              </div>
            </div>
          </div>

          {/* Content Creation Section */}
          <div className={sectionClass}>
            <div className="space-y-6">
              {/* Multiple Links Section */}
              <div>
                <label className={labelClass}>
                  Links <span className="text-red-300">*</span>
                </label>
                <p className={helperTextClass}>
                  Add links to your content, social profiles, or website
                </p>
                
                <div className="space-y-3">
                  {formData.portfolio.map((link, index) => (
                    <div key={index} className="flex gap-2">
                      <div className="flex-1">
                        <input
                          type="url"
                          value={link}
                          onChange={(e) => handleLinkChange(index, e.target.value)}
                          placeholder="https://"
                          className={`${inputClass} ${link && !validateURL(link) ? 'border-red-500' : ''}`}
                          required={index === 0}
                        />
                        {link && !validateURL(link) && (
                          <p className={errorClass}>Please enter a valid URL starting with http:// or https://</p>
                        )}
                      </div>
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveLink(index)}
                          className="px-3 py-2 text-[#F7E8CC] hover:text-red-300 transition-colors"
                          aria-label="Remove link"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                
                <button
                  type="button"
                  onClick={handleAddLink}
                  className={`${buttonClass} mt-3`}
                >
                  + Add Another Link
                </button>
              </div>

              {/* Languages and ShowsFace fields remain the same */}
              {/* ... existing language and showsFace fields ... */}
            </div>
          </div>

          {/* Contact Section - Simplified */}
          <div className={sectionClass}>
            <div className="space-y-6">
              <div>
                <label className={labelClass}>
                  Phone Number <span className="text-red-300">*</span>
                </label>
                <input
                  type="tel"
                  required
                  value={formData.contact?.phoneNumber || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    contact: { ...formData.contact, phoneNumber: e.target.value }
                  })}
                  placeholder="(555) 555-5555"
                  className={inputClass}
                />
                {errors['contact.phone'] && (
                  <p className={errorClass}>{errors['contact.phone']}</p>
                )}
              </div>
              
              {/* Optional Discord field */}
              <div>
                <label className={labelClass}>
                  Discord ID (Optional)
                </label>
                <input
                  type="text"
                  value={formData.contact?.discord || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    contact: { ...formData.contact, discord: e.target.value }
                  })}
                  placeholder="username"
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* Referral Section */}
          <div className={sectionClass}>
            <div className="space-y-6">
              <div>
                <label className={labelClass}>
                  How did you hear about us?
                </label>
                <select
                  value={formData.referralSource || ''}
                  onChange={updateFormField('referralSource')}
                  className={selectClass}
                >
                  <option value="">Select an option</option>
                  <option value="Ashli">Ashli (@affiliatewithash or @affilseldcr)</option>
                  <option value="YFD">YFD (Your First Dollar)</option>
                  <option value="Email">Email</option>
                  <option value="DM">DM</option>
                  <option value="Handshake">Handshake</option>
                  <option value="Playbill">Playbill</option>
                  <option value="Backstage">Backstage</option>
                  <option value="Tisch">Tisch</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex justify-center rounded-md border border-[#F7E8CC]/10 bg-[#5E150F] py-3 px-6 text-base font-medium text-[#F7E8CC] shadow-sm hover:bg-[#4A110D] focus:outline-none focus:ring-2 focus:ring-[#F7E8CC]/10 focus:ring-offset-2 focus:ring-offset-[#7B1C14] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        </form>

        <div className="mt-8 text-center">
          <p className="text-[#F7E8CC]/80 text-center">
            For questions or support please contact{' '}
            <a 
              href="mailto:ashli@spicycubes.co" 
              className="font-bold text-[#F7E8CC] hover:text-[#F7E8CC] whitespace-nowrap underline decoration-[2px] hover:decoration-[2.5px] transition-all duration-200 [text-decoration-skip-ink:none]"
            >
              ashli@spicycubes.co
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

// Remove Sentry profiler wrapper and export component directly
export const ApplicationForm = ApplicationFormComponent; 